import { EYE_LAYER_POLYGONS, IRIS_INDICES } from '../avatarInspector/featureIndices'

const clamp = (value, min, max) => Math.max(min, Math.min(max, value))

const EYE_ANCHORS = {
  left: {
    outer: 33,
    inner: 133,
    upper: 159,
    lower: 145
  },
  right: {
    outer: 263,
    inner: 362,
    upper: 386,
    lower: 374
  }
}

const getPoint = (points, idx) =>
  Number.isInteger(idx) && idx >= 0 && idx < (points?.length || 0) ? points[idx] || null : null

const averagePoints = (points) => {
  if (!points?.length) return null
  let sx = 0
  let sy = 0
  let count = 0
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i]
    if (!p) continue
    sx += p.x
    sy += p.y
    count += 1
  }
  if (!count) return null
  return { x: sx / count, y: sy / count }
}

const computeBounds = (points) => {
  if (!points?.length) return null
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  let count = 0
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i]
    if (!p) continue
    if (p.x < minX) minX = p.x
    if (p.y < minY) minY = p.y
    if (p.x > maxX) maxX = p.x
    if (p.y > maxY) maxY = p.y
    count += 1
  }
  if (!count) return null
  return { minX, minY, maxX, maxY, width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) }
}

const pointInExpandedBounds = (point, bounds, expand) => {
  if (!point || !bounds) return false
  return (
    point.x >= bounds.minX - expand &&
    point.x <= bounds.maxX + expand &&
    point.y >= bounds.minY - expand &&
    point.y <= bounds.maxY + expand
  )
}

const resolveEyeCircle = (pointsPx, side) => {
  const poly = side === 'left' ? EYE_LAYER_POLYGONS.left : EYE_LAYER_POLYGONS.right
  const centerIdx = side === 'left' ? IRIS_INDICES.leftCenter : IRIS_INDICES.rightCenter
  const ring = side === 'left' ? IRIS_INDICES.leftRing : IRIS_INDICES.rightRing
  const anchors = EYE_ANCHORS[side]

  const socketPoints = []
  for (let i = 0; i < poly.length; i += 1) {
    const p = getPoint(pointsPx, poly[i])
    if (p) socketPoints.push(p)
  }
  if (socketPoints.length < 4) return null

  const bounds = computeBounds(socketPoints)
  if (!bounds) return null

  const anchorPoints = [
    getPoint(pointsPx, anchors.outer),
    getPoint(pointsPx, anchors.inner),
    getPoint(pointsPx, anchors.upper),
    getPoint(pointsPx, anchors.lower)
  ].filter(Boolean)

  const fallbackCenter = averagePoints(anchorPoints) || averagePoints(socketPoints)
  if (!fallbackCenter) return null

  const expand = Math.max(bounds.width, bounds.height) * 0.28
  const irisCenter = getPoint(pointsPx, centerIdx)
  const centerRaw = pointInExpandedBounds(irisCenter, bounds, expand) ? irisCenter : fallbackCenter

  const insetX = bounds.width * 0.1
  const insetY = bounds.height * 0.1
  const center = {
    x: clamp(centerRaw.x, bounds.minX + insetX, bounds.maxX - insetX),
    y: clamp(centerRaw.y, bounds.minY + insetY, bounds.maxY - insetY)
  }

  let ringRadius = 0
  let ringCount = 0
  for (let i = 0; i < ring.length; i += 1) {
    const rp = getPoint(pointsPx, ring[i])
    if (!rp || !pointInExpandedBounds(rp, bounds, expand)) continue
    ringRadius += Math.hypot(rp.x - center.x, rp.y - center.y)
    ringCount += 1
  }
  ringRadius = ringCount > 0 ? ringRadius / ringCount : 0

  const socketMin = Math.max(1, Math.min(bounds.width, bounds.height))
  const minRadius = Math.max(1.2, socketMin * 0.1)
  const maxRadius = Math.max(minRadius + 0.5, socketMin * 0.38)
  const fallbackRadius = clamp(socketMin * 0.235, minRadius, maxRadius)
  const radius = clamp(ringRadius > 0 ? ringRadius : fallbackRadius, minRadius, maxRadius)

  return { cx: center.x, cy: center.y, radius }
}

const rasterizeSoftCircle = (alpha, width, height, cx, cy, radius, softEdgePx, growPx) => {
  const soft = Math.max(0.5, Number(softEdgePx) || 1.2)
  const grow = Math.max(0, Number(growPx) || 0)
  const r = Math.max(0.8, radius + grow)
  const inner = Math.max(0, r - soft)
  const outer = r + soft
  const minX = Math.max(0, Math.floor(cx - outer - 1))
  const maxX = Math.min(width - 1, Math.ceil(cx + outer + 1))
  const minY = Math.max(0, Math.floor(cy - outer - 1))
  const maxY = Math.min(height - 1, Math.ceil(cy + outer + 1))
  const inv = outer > inner ? 1 / (outer - inner) : 1

  for (let y = minY; y <= maxY; y += 1) {
    const dy = y - cy
    for (let x = minX; x <= maxX; x += 1) {
      const dx = x - cx
      const dist = Math.hypot(dx, dy)
      if (dist >= outer) continue
      const idx = y * width + x
      let value = 255
      if (dist > inner) {
        value = Math.round((1 - (dist - inner) * inv) * 255)
      }
      if (value > alpha[idx]) alpha[idx] = value
    }
  }
}

export const buildIrisCutAlphaMask = ({
  width,
  height,
  pointsPx,
  eyeAlpha = null,
  softEdgePx = 1.2,
  growPx = 0.35
} = {}) => {
  const w = Math.max(1, Math.round(width || 0))
  const h = Math.max(1, Math.round(height || 0))
  const alpha = new Uint8ClampedArray(w * h)
  if (!pointsPx?.length) return alpha

  const left = resolveEyeCircle(pointsPx, 'left')
  const right = resolveEyeCircle(pointsPx, 'right')
  if (left) rasterizeSoftCircle(alpha, w, h, left.cx, left.cy, left.radius, softEdgePx, growPx)
  if (right) rasterizeSoftCircle(alpha, w, h, right.cx, right.cy, right.radius, softEdgePx, growPx)

  if (eyeAlpha?.length === alpha.length) {
    for (let i = 0; i < alpha.length; i += 1) {
      alpha[i] = Math.round((alpha[i] * (eyeAlpha[i] || 0)) / 255)
    }
  }

  return alpha
}

export const applyAlphaCutout = (baseAlpha, cutAlpha) => {
  if (!baseAlpha?.length) return new Uint8ClampedArray()
  const out = new Uint8ClampedArray(baseAlpha.length)
  if (!cutAlpha?.length) {
    out.set(baseAlpha)
    return out
  }
  const len = Math.min(baseAlpha.length, cutAlpha.length)
  for (let i = 0; i < len; i += 1) {
    const base = baseAlpha[i] || 0
    const cut = cutAlpha[i] || 0
    out[i] = Math.round((base * (255 - cut)) / 255)
  }
  for (let i = len; i < baseAlpha.length; i += 1) out[i] = baseAlpha[i] || 0
  return out
}

export default {
  buildIrisCutAlphaMask,
  applyAlphaCutout
}
